/**
 * aethereum sync-rules — fold the room's team ruleset into the local AGENTS.md.
 *
 * The ruleset (aethereum__set_ruleset, CAP-5) is the team's living AGENTS.md: it
 * rides get_team_context for every agent, and this command has been advertised
 * in the tool description and the live push nudge ("run `aethereum sync-rules`
 * to pick it up") since v3 — but the command itself was never registered. This
 * closes that gap: fetch the current ruleset via GET /api/sync/context?full=1
 * (non-consuming) and write it into a marked, idempotent AGENTS.md section.
 *
 * Fail-soft: not configured / offline / no ruleset prints a clear message and
 * exits 0 (never breaks a script or an agent that shells out to it).
 */

import { readFileSync, writeFileSync, existsSync, appendFileSync } from "node:fs";
import { join } from "node:path";
import { loadAutoConfig } from "../hive/auto.js";

const RULES_BEGIN = "<!-- aethereum:ruleset:begin -->";
const RULES_END = "<!-- aethereum:ruleset:end -->";

export interface SyncRulesOptions {
  cwd?: string;
  env?: Record<string, string | undefined>;
  home?: string;
  fetchImpl?: typeof fetch;
  out?: (line: string) => void;
}

/** Build the marked AGENTS.md ruleset section. Exported for tests. */
export function rulesetSection(text: string, version?: number): string {
  return `${RULES_BEGIN}
## Team ruleset${version ? ` (v${version})` : ""}

These rules were agreed by the team (aethereum__set_ruleset) and apply to every
agent in this room. Re-sync anytime with \`aethereum sync-rules\`.

${text.trim()}
${RULES_END}`;
}

/** Create or refresh the marked ruleset block in AGENTS.md. Idempotent: replaces
 *  the block in place, appends when absent, creates the file when missing.
 *  Exported for tests. Returns what happened. */
export function foldRulesetIntoAgentsMd(
  cwd: string,
  text: string,
  version?: number,
): "created" | "updated" | "appended" {
  const path = join(cwd, "AGENTS.md");
  const section = rulesetSection(text, version);
  if (!existsSync(path)) {
    writeFileSync(path, section + "\n", { mode: 0o644 });
    return "created";
  }
  const content = readFileSync(path, "utf8");
  const i = content.indexOf(RULES_BEGIN);
  const j = content.indexOf(RULES_END);
  if (i !== -1 && j !== -1) {
    const next = content.slice(0, i) + section + content.slice(j + RULES_END.length);
    writeFileSync(path, next, { mode: 0o644 });
    return "updated";
  }
  appendFileSync(path, (content.endsWith("\n") ? "\n" : "\n\n") + section + "\n");
  return "appended";
}

export async function runSyncRules(options: SyncRulesOptions = {}): Promise<number> {
  const out = options.out ?? ((line: string) => console.log(line));
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);

  const cfg = loadAutoConfig(cwd, env, options.home);
  if (!cfg) {
    out("No room configured here. Run 'aethereum init' first; then teammates' rulesets can sync.");
    return 0;
  }

  let ruleset: { text?: string; version?: number } | null = null;
  try {
    const res = await (options.fetchImpl ?? fetch)(`${cfg.url}/api/sync/context?full=1`, {
      headers: { Authorization: `Bearer ${cfg.token}` },
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) {
      out(`Could not read the team context (${res.status}). Nothing changed; try again later.`);
      return 0;
    }
    const body = (await res.json().catch(() => ({}))) as {
      context?: { ruleset?: { text?: string; version?: number } | null };
    };
    ruleset = body.context?.ruleset ?? null;
  } catch {
    out("Could not reach Aethereum (offline?). Nothing changed; try again when you are online.");
    return 0;
  }

  if (!ruleset?.text?.trim()) {
    out("This room has no team ruleset yet. An agent sets one with the aethereum__set_ruleset tool.");
    return 0;
  }

  try {
    const what = foldRulesetIntoAgentsMd(cwd, ruleset.text, ruleset.version);
    out(
      `[ok] team ruleset${ruleset.version ? ` v${ruleset.version}` : ""} folded into AGENTS.md (${what}).`,
    );
  } catch (err) {
    out(`Could not write AGENTS.md (${err instanceof Error ? err.message : String(err)}). Nothing synced.`);
  }
  return 0;
}
